import { useEffect, useRef, useState } from "react";
import { useFormContext, Controller } from "react-hook-form";
import { InputWrapper, ScoreButtonsWrapper } from "styles/jss/Forms.styles";
import { CustomTextField } from "styles/jss/Inputs.styles";
import { PAsTitle } from "styles/jss/FormPages.styles";
import {
  Grid,
  Button,
  MenuItem,
  FormControl,
  FormLabel,
  FormControlLabel,
  Checkbox,
  Radio,
  RadioGroup,
} from "@mui/material";
import { CITIES, PROVINCES } from "utils/external_data";
import AdapterJalali from "@date-io/date-fns-jalali";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";

export const FormInput = ({ name, ...otherProps }) => {
  const {
    control,
    formState: { errors },
  } = useFormContext();

  return (
    <InputWrapper
      hasError={!!errors[name]}
      className={"inputWrapper"}
      hasLabel={!!otherProps.label}
    >
      <Controller
        control={control}
        name={name}
        defaultValue=""
        render={({ field }) => (
          <CustomTextField
            {...field}
            variant="outlined"
            sx={{
              backgroundColor: "#FFF",
              zIndex: 1,
            }}
            error={!!errors[name]}
            {...otherProps}
          />
        )}
      />
    </InputWrapper>
  );
};

export const FormSelectInput = ({ name, ...otherProps }) => {
  const {
    control,
    formState: { errors },
  } = useFormContext();

  const options = otherProps.options || [
    { label: "1", value: 1 },
    { label: "2", value: 2 },
    { label: "3", value: 3 },
  ];

  return (
    <InputWrapper
      hasError={!!errors[name]}
      className={"inputWrapper"}
      hasLabel={!!otherProps.label}
    >
      <Controller
        control={control}
        name={name}
        {...(!!otherProps.defaultValue
          ? {
              defaultValue: otherProps.defaultValue,
            }
          : { defaultValue: "" })}
        render={({ field }) => (
          <CustomTextField
            select
            {...field}
            {...otherProps}
            variant="outlined"
            sx={{
              backgroundColor: "#FFF",
              zIndex: 1,
            }}
            error={!!errors[name]}
          >
            {options.map(option => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </CustomTextField>
        )}
      />
    </InputWrapper>
  );
};

export const FormSelectCity = ({ provinceLabel, cityLabel }) => {
  const [city, setCity] = useState([]);
  const { watch } = useFormContext();

  const watchShowProvince = watch("province");

  const province = PROVINCES.map(item => {
    return {
      value: item.id,
      label: item.name,
    };
  });

  useEffect(() => {
    setCity(
      CITIES.filter(item => item.province_id === +watchShowProvince).map(
        item => {
          return {
            value: item.id,
            label: item.name,
          };
        },
      ),
    );
  }, [watchShowProvince]);

  return (
    <>
      <Grid item xs={12} md={6}>
        <FormSelectInput
          label={!!provinceLabel ? provinceLabel : "استان"}
          name="province"
          options={province}
          fullWidth
          required
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <FormSelectInput
          label={!!cityLabel ? cityLabel : "شهر"}
          name="city"
          options={city}
          fullWidth
          required
        />
      </Grid>
    </>
  );
};

export const FileInput = ({ name, ...otherProps }) => {
  const inputFile = useRef(null);
  const {
    register,
    formState: { errors },
    watch,
  } = useFormContext();
  const { onChange, ref } = register(name);

  const handleFile = evt => {
    evt.preventDefault();
    inputFile.current.click();
  };

  const handleChange = evt => {
    onChange(evt);
  };

  return (
    <InputWrapper className={"inputWrapper"} hasError={!!errors[name]}>
      <div className={`fileInput`}>
        <input
          name={name}
          type="file"
          style={{ display: "none" }}
          ref={e => {
            ref(e);
            inputFile.current = e; // you can still assign to ref
          }}
          onChange={handleChange}
          {...(!!otherProps?.accept ? {accept:otherProps?.accept}:{})}
        />
        <Button
          className={"select"}
          onClick={handleFile}
          style={{
            width: !!watch(name)?.length ? "100%" : "auto",
          }}
        >
          {!!watch(name)?.length ? watch(name)[0].name : otherProps.btnText}
        </Button>
      </div>
      {!!errors[name] && (
        <span className={"error-inputFile"}>{errors[name]?.message}</span>
      )}
    </InputWrapper>
  );
};

export const FormScoreRadioGroup = ({ name, title, score }) => {
  const values = !!score
    ? Array.from(Array(score).keys(), item => item + 1)
    : [1, 2, 3, 4, 5, 6, 7];
  const {
    register,
    formState: { errors },
    watch,
  } = useFormContext();

  const [value, setValue] = useState(watch(name));

  return (
    <ScoreButtonsWrapper>
      <PAsTitle>{title}</PAsTitle>
      <div className="scoreWrapper">
        {values.map(item => {
          return (
            <label
              key={item}
              htmlFor={`${name}_${item}`}
              className={
                !!errors[name] ? "error" : value === item ? "selected" : ""
              }
              onClick={() => setValue(item)}
            >
              <input
                id={`${name}_${item}`}
                type={"radio"}
                value={item}
                {...register(name)}
              />
              <span>{item}</span>
            </label>
          );
        })}
      </div>
    </ScoreButtonsWrapper>
  );
};

export const FormRadioGroup = ({ name, ...otherProps }) => {
  const {
    control,
    formState: { errors },
  } = useFormContext();

  const options = otherProps.options || [
    { label: "1 label", value: "1" },
    { label: "2 label", value: "2" },
    { label: "3 label", value: "3" },
    { label: "4 label", value: "4" },
  ];

  return (
    <InputWrapper
      hasError={!!errors[name]}
      className={"inputWrapper"}
      hasLabel={!!otherProps.label}
    >
      <Controller
        control={control}
        name={name}
        defaultValue=""
        render={({ field }) => (
          <FormControl
            fullWidth
            error={!!errors[name]}
            required={otherProps.required}
            component="fieldset"
          >
            <FormLabel component="legend">{otherProps.label}</FormLabel>
            <RadioGroup row value={field.value} {...field} {...otherProps}>
              {options.map(option => (
                <FormControlLabel
                  key={option.value}
                  value={option.value}
                  control={<Radio />}
                  label={option.label}
                />
              ))}
            </RadioGroup>
          </FormControl>
        )}
      />
    </InputWrapper>
  );
};

export const FormCheckBoxGroup = ({ title, name, ...otherProps }) => {
  const {
    control,
    formState: { errors },
  } = useFormContext();

  const options = otherProps.options || [
    { label: "1 label", value: "1" },
    { label: "2 label", value: "2" },
    { label: "3 label", value: "3" },
    { label: "4 label", value: "4" },
  ];

  return (
    <InputWrapper
      hasError={!!errors[name]}
      className={"inputWrapper"}
      hasLabel={!!otherProps.label}
    >
      <FormControl
        fullWidth
        error={!!errors[name]}
        required={otherProps.required}
      >
        <FormLabel>{title}</FormLabel>
        <Controller
          control={control}
          name={name}
          render={({ field }) => (
            <Grid container className={"checkBoxGroupWrapper"}>
              {options.map(item => (
                <Grid key={item.value} item xs={6}>
                  <FormControlLabel
                    key={item.value}
                    label={item.label}
                    control={
                      <Checkbox
                        value={item.value}
                        checked={
                          field?.value?.some(
                            existingValue => existingValue === item.value,
                          ) || false
                        }
                        onChange={(event, checked) => {
                          if (checked) {
                            field.onChange([
                              ...(field.value || []),
                              event.target.value,
                            ]);
                          } else {
                            field.onChange(
                              field.value.filter(
                                value => value !== event.target.value,
                              ),
                            );
                          }
                        }}
                      />
                    }
                  />
                </Grid>
              ))}
            </Grid>
          )}
        />
      </FormControl>
    </InputWrapper>
  );
};

export const CustomDatePicker = ({ name, ...otherProps }) => {
  const {
    formState: { errors },
    control,
    watch,
  } = useFormContext();
  const [value, setValue] = useState(watch(name) || null);

  return (
    <InputWrapper
      hasError={!!errors[name]}
      className={"inputWrapper"}
      hasLabel={!!otherProps.label}
    >
      <Controller
        name={name}
        control={control}
        render={({ field: { onChange, ...restField } }) => (
          <LocalizationProvider
            dateAdapter={
              otherProps.locale === "fa" ? AdapterJalali : AdapterMoment
            }
          >
            <DatePicker
              value={value}
              onChange={newValue => {
                setValue(newValue);
                onChange(newValue);
              }}
              renderInput={params => (
                <CustomTextField {...params} {...otherProps} />
              )}
              className={"datePicker"}
            />
          </LocalizationProvider>
        )}
      />
    </InputWrapper>
  );
};
